import type { EntityState } from '@ngrx/entity';
import type { School, SchoolActivity, SchoolActivityInstance } from '../../models';
import type { SchoolActivityInstanceOverview } from '../../services/flex-daily-overview.service';
import type { SchoolActivityAttendee } from '../../services/school-activity.service';
import { accountsReducer } from '../accounts/reducers';
import type { IAccountsState } from '../accounts/states/accounts.state';
import type { AttendanceRecordState } from '../attendance';
import { attendanceReducer } from '../attendance';
import { colorsReducer } from '../color-profiles/reducers';
import type { ColorsState } from '../color-profiles/states';
import { contactTraceReducer } from '../contact-trace/reducers';
import type { IContactTraceStates } from '../contact-trace/states';
import type { EncounterDetectionState } from '../encounter-detection';
import { encounterDetectionReducer } from '../encounter-detection/reducers';
import { exclusionGroupsReducer } from '../encounters-prevention/excusion-groups/reducers/exclusion-groups.reducer';
import type { IExclusionGroupsState } from '../encounters-prevention/excusion-groups/states';
import { favoriteLocationsReducer } from '../favorite-locations/reducers';
import type { FavoriteLocationsState } from '../favorite-locations/states';
import type { FlexPeriodState } from '../flex-periods';
import { flexPeriodsReducer } from '../flex-periods';
import type { IIntrosState } from '../intros';
import { IntrosReducer } from '../intros';
import { kioskReducer } from '../kiosk/kiosk.reducer';
import type { KioskState } from '../kiosk/kiosk.state';
import type { LocationActivePassesState } from '../location-active-passes/location-active-passes.reducer';
import { locationActivePassesReducer } from '../location-active-passes/location-active-passes.reducer';
import { locationsReducer } from '../locations/locations.reducer';
import type { LocationsState } from '../locations/locations.state';
import { onboardProcessReducer } from '../onboard-process/reducers';
import type { ProcessState } from '../onboard-process/states';
import { PassFiltersReducer } from '../pass-filters/reducers';
import type { IFiltersState } from '../pass-filters/states';
import { passLikeCollectionReducer } from '../pass-like-collection/reducers';
import type { IPassLikeCollectionState } from '../pass-like-collection/states/pass-like-collection.state';
import { passLimitsReducer } from '../pass-limits/reducers';
import type { IPassLimitState } from '../pass-limits/states';
import type { IPassesState } from '../passes';
import { passesReducer } from '../passes';
import { profilePicturesReducer } from '../profile-pictures/reducers';
import type { IProfilePicturesState } from '../profile-pictures/states';
import { quickPreviewPassesReducer } from '../quick-preview-passes/reducers';
import type { IQuickPreviewPassesState } from '../quick-preview-passes/states';
import type { ReportsState } from '../reports';
import { reportsReducer } from '../reports/reducers';
import { representedUsersReducer } from '../represented-users/reducers';
import type { IRepresentedUsersState } from '../represented-users/states/represented-users.state';
import { type State as ScheduleEditorState, reducer as scheduleEditorReducer } from '../schedule-editor/schedule-editor.reducer';
import { schoolActivitiesReducer } from '../school-activities';
import { schoolActivitiesInstancesReducer } from '../school-activities-instances';
import { schoolActivitiesInstanceOverviewReducer } from '../school-activities-instances-overview/reducers/school-activities-instances-overview.reducer';
import { schoolActivityAttendeesReducer } from '../school-activity-attendees';
import { schoolsReducer } from '../schools/reducers';
import type { SchoolsState } from '../schools/states/schools.state';
import { studentGroupsReducer } from '../student-groups/reducers';
import type { GroupsStates } from '../student-groups/states';
import { teacherLocationsReducer } from '../teacherLocations/reducers';
import { toastReducer } from '../toast/reducers';
import type { IToastState } from '../toast/states/toast.state';
import { userReducer } from '../user/reducers/user.reducer';
import type { UserState } from '../user/states';

export interface AppState {
	readonly accounts: IAccountsState;
	readonly attendance: AttendanceRecordState;
	readonly colorProfiles: ColorsState;
	readonly contactTrace: IContactTraceStates;
	readonly encounterDetection: EncounterDetectionState;
	readonly exclusionGroups: IExclusionGroupsState;
	readonly favoriteLocations: FavoriteLocationsState;
	readonly flexPeriods: FlexPeriodState;
	readonly intros: IIntrosState;
	readonly kiosk: KioskState;
	readonly locations: LocationsState;
	readonly locationActivePasses: LocationActivePassesState;
	readonly onboardProcess: ProcessState;
	readonly pass_filters: IFiltersState;
	readonly pass_limits: IPassLimitState;
	readonly passes: IPassesState;
	readonly passLikeCollection: IPassLikeCollectionState;
	readonly profilePictures: IProfilePicturesState;
	readonly quickPreviewPasses: IQuickPreviewPassesState;
	readonly reports: ReportsState;
	readonly representedUsers: IRepresentedUsersState;
	readonly scheduleEditor: ScheduleEditorState;
	readonly school: School[];
	readonly schoolActivities: EntityState<SchoolActivity>;
	readonly schoolActivityAttendees: EntityState<SchoolActivityAttendee>;
	readonly schoolActivityInstances: EntityState<SchoolActivityInstance>;
	readonly schoolActivityInstanceOverview: EntityState<SchoolActivityInstanceOverview>;
	readonly schools: SchoolsState;
	readonly studentGroups: GroupsStates;
	readonly teacherLocations: LocationsState;
	readonly toast: IToastState;
	readonly user: UserState;
}

export const reducers = {
	accounts: accountsReducer,
	attendance: attendanceReducer,
	colorProfiles: colorsReducer,
	contactTrace: contactTraceReducer,
	encounterDetection: encounterDetectionReducer,
	exclusionGroups: exclusionGroupsReducer,
	favoriteLocations: favoriteLocationsReducer,
	flexPeriods: flexPeriodsReducer,
	intros: IntrosReducer,
	kiosk: kioskReducer,
	locations: locationsReducer,
	locationActivePasses: locationActivePassesReducer,
	onboardProcess: onboardProcessReducer,
	pass_filters: PassFiltersReducer,
	pass_limits: passLimitsReducer,
	passes: passesReducer,
	passLikeCollection: passLikeCollectionReducer,
	profilePictures: profilePicturesReducer,
	quickPreviewPasses: quickPreviewPassesReducer,
	reports: reportsReducer,
	representedUsers: representedUsersReducer,
	scheduleEditor: scheduleEditorReducer,
	schoolActivities: schoolActivitiesReducer,
	schoolActivityAttendees: schoolActivityAttendeesReducer,
	schoolActivityInstances: schoolActivitiesInstancesReducer,
	schoolActivityInstanceOverview: schoolActivitiesInstanceOverviewReducer,
	schools: schoolsReducer,
	studentGroups: studentGroupsReducer,
	teacherLocations: teacherLocationsReducer,
	toast: toastReducer,
	user: userReducer,
};
